import {
  createMessage,
  SETUP_TEXT,
  PLANS_TEXT,
  BILLING_TEXT,
  MEMBERS_TEXT,
  BILLING_SEAT_BASED_TEXT,
  SECURITY_REPORTS_TEXT,
  BILLING_CONSOLE_TEXT,
  SECURITY_TEXT,
  SUPPORT_TEXT,
  SUPPORT_ACCESS_TEXT,
} from "constants/Messages";
import { useSelector } from "react-redux";
import {
  getFlowType,
  shouldShowBillingUsage,
  shouldShowMembers,
  shouldShowPlans,
} from "selectors/LicenseSelectors";
import { getIsSupportActor } from "selectors/AccountSelectors";
import { SUPPORT_ACCESS } from "constants/RouteContants";
import { isSupportAccessActive } from "utils/SupportAccessUtils";
import analyticEvents from "constants/AnalyticEvents";
import { FlowType } from "types/LicenseTypes";

export interface SidebarItem {
  id: string;
  text: string;
  path: string;
  isVisible: boolean;
  icon: string;
  segmentEvent: string;
}

export interface SidebarSection {
  id: string;
  title: string;
  items: SidebarItem[];
}

export default function useSidebarItems(): SidebarSection[] {
  const showBillingUsagePage = useSelector(shouldShowBillingUsage);
  const showPlansPage = useSelector(shouldShowPlans);
  const showMembersPage = useSelector(shouldShowMembers);
  const flowType = useSelector(getFlowType);
  const isSupportActor = useSelector(getIsSupportActor);
  // Hidden during a delegated session: the portal then renders as the
  // customer, who must never see support tooling.
  const showSupportSection = isSupportActor && !isSupportAccessActive();

  const sidebarSections: SidebarSection[] = [
    {
      id: "billing-console",
      title: createMessage(BILLING_CONSOLE_TEXT),
      items: [
        {
          id: "license",
          text: createMessage(SETUP_TEXT),
          path: "/license",
          isVisible: true,
          icon: "key-2-line",
          segmentEvent: analyticEvents.LICENSE_PAGE_CLICK_PORTAL,
        },
        {
          id: "plans",
          text: createMessage(PLANS_TEXT),
          path: "/plans",
          isVisible: Boolean(showPlansPage),
          icon: "bill-line",
          segmentEvent: analyticEvents.PLAN_BUILDER_CLICK_PORTAL,
        },
        {
          id: "billing",
          text:
            flowType === FlowType.SEAT_BASED_UPDATE ||
            flowType === FlowType.SEAT_BASED
              ? createMessage(BILLING_SEAT_BASED_TEXT)
              : createMessage(BILLING_TEXT),
          path: "/billing",
          isVisible: Boolean(showBillingUsagePage),
          icon: "file-paper-2-line",
          segmentEvent: analyticEvents.USAGE_PAGE_CLICK_PORTAL,
        },
        {
          id: "members",
          text: createMessage(MEMBERS_TEXT),
          path: "/members",
          isVisible: Boolean(showMembersPage),
          icon: "user-3-line",
          segmentEvent: analyticEvents.MEMBERS_PAGE_CLICK_PORTAL,
        },
      ],
    },
    {
      id: "security",
      title: createMessage(SECURITY_TEXT),
      items: [
        {
          id: "security-reports",
          text: createMessage(SECURITY_REPORTS_TEXT),
          path: "/security-reports",
          isVisible: true,
          icon: "lock-2-line",
          segmentEvent: analyticEvents.MEMBERS_PAGE_CLICK_PORTAL,
        },
      ],
    },
    {
      id: "support",
      title: createMessage(SUPPORT_TEXT),
      items: [
        {
          id: "support-access",
          text: createMessage(SUPPORT_ACCESS_TEXT),
          path: SUPPORT_ACCESS,
          isVisible: showSupportSection,
          icon: "user-shared-line",
          segmentEvent: analyticEvents.SUPPORT_ACCESS_PAGE_CLICK_PORTAL,
        },
      ],
    },
  ];

  return sidebarSections;
}
